/**
 * Vuex
 *
 * @library
 *
 * https://vuex.vuejs.org/en/
 */

// Lib imports
import Vue from 'vue'
import Vuex from 'vuex'

// Store functionality
import modules from './modules'

import { hasRole } from '../helpers/has'

Vue.use(Vuex)

// Create a new store
const store = new Vuex.Store({
  modules,
  state: {
    loggedIn: false,
    user: { },
    settings: {
      darkMode: false,
      miniMenu: false
    },
    roles: {},
    lastActivity: null,
    referrer: '',
    snackbar: {
      active: false,
      color: '',
      timeout: 3000,
      text: '',
      action: '',
      actionText: ''
    },
    checklist: [],
    trackerId: '',
    tracker: {
      id: '',
      sections: [],
      blocks: []
    },
    academics: {
      admitCode: '',
      major: [],
      minor: [],
      conc: [],
      credits: {},
      mathLevel: null,
      math: {
        level: 0,
        T1: null,
        T2: null,
        T3: null
      },
      tests: [],
      preReg: false
    },
    actions: [],
    actionNavBarActive: false,
    term: '',
    linkToLoad: '',
    offline: true
  },
  mutations: {
    updateActivity: (state) => {
      state.lastActivity = new Date().getTime()
      window.localStorage.lastActivity = state.lastActivity
    },
    clearActivity (state) {
      state.lastActivity = null
      window.localStorage.lastActivity = null
    },
    setUser (state, user) {
      state.user = user
    },
    updateSettings (state, settings) {
      state.settings = settings
    },
    updateSetting (state, { field, value }) {
      state.settings[field] = value
    },
    setRoles (state, roles) {
      // Convert the roles array into an object and save in the store
      let obj = {}
      for (let i = 0; i < roles.length; i++) {
        obj[roles[i]] = true
      }
      state.roles = obj
    },
    setLoggedIn (state, loggedIn) {
      state.loggedIn = loggedIn
    },
    setReferrer (state, referrer) {
      state.referrer = referrer
    },
    setSnackbar: (state, snackbar) => {
      state.snackbar = snackbar
    },
    setChecklist: (state, checklist) => {
      state.checklist = checklist
    },
    updateChecklistItem: (state, { index, code, status, lastSlide }) => {
      if (index != null) {
        let temp = Object.assign({}, state.checklist[index])
        if (status != null) temp.status = status
        if (lastSlide != null) temp.lastSlide = lastSlide
        state.checklist.splice(index, 1, temp)
      } else {
        for (let i = 0; i < state.checklist.length; i++) {
          if (state.checklist[i].code === code) {
            let temp = Object.assign({}, state.checklist[i])
            if (status != null) temp.status = status
            if (lastSlide != null) temp.lastSlide = lastSlide
            state.checklist.splice(i, 1, temp)
          }
        }
      }
    },
    setTrackerId: (state, id) => {
      state.trackerId = id
      state.tracker.id = id
    },
    setTrackerSections: (state, sections) => {
      state.tracker.sections = sections
    },
    setTrackerBlocks: (state, blocks) => {
      state.tracker.blocks = blocks
    },
    setTerm: (state, term) => {
      state.term = term
    },
    setActions: (state, actions) => {
      state.actions = actions
    },
    addAction: (state, action) => {
      state.actions.push(action)
    },
    setActionNavBarActive: (state, active) => {
      state.actionNavBarActive = active
    },
    setLink: (state, link) => {
      state.linkToLoad = link
    },
    setOffline: (state, offline) => {
      state.offline = offline
    },
    setAcademics: (state, academics) => {
      state.academics = academics
    }
  },
  getters: {
    hasRole: (state) => (role) => {
      if (typeof (role) === 'string') {
        return (role in state.roles)
      } else if (typeof (role) === 'object' && 'length' in role) {
        for (let j = 0; j < role.length; j++) {
          if (role[j] in state.roles) return true
        }
      }
      return false
    },
    isAdmin: (state) => {
      return hasRole(state.user, ['Student Development', 'Technology Services', 'Marketing & Communications', 'Ofc. of Marketing & Communications', 'Communications Dept'])
    }
  },
  actions: {
    login (context, user) {
      if (user && 'username' in user) {
        context.commit('setLoggedIn', true)
        context.commit('setUser', user)
        context.commit('setRoles', 'spoof' in user ? user.spoof.roles : user.roles || [])
        if ('settings' in user) {
          for (let field in user.settings) {
            context.commit('updateSetting', { field, value: user.settings[field] })
          }
        }
        context.commit('updateActivity')
        context.dispatch('loadTracker')
      }
    },
    logout (context) {
      context.commit('setLoggedIn', false)
      context.commit('setUser', {})
      context.commit('setRoles', [])
      context.commit('clearActivity')
      context.commit('updateSetting', { field: 'darkMode', value: false })
    },
    updateUser (context, user) {
      if ('_id' in context.state.user) {
        // Update the user in the database and in the vuex store once complete
        this.$feathers.service('users').update(context.state.user._id, user).then((data) => {
          context.commit('setUser', user)
        })
      } else {
        context.commit('setUser', user)
      }
    },
    updateUserSetting (context, { field, value }) {
      context.commit('updateSetting', { field, value })
      let settings = context.state.settings
      settings[field] = value
      let user = context.state.user
      user.settings = settings
      context.dispatch('updateUser', user)
    },
    snackbar (context, { active, color, timeout, text, actionText, action }) {
      let snackbar = Object.assign({}, context.snackbar)
      if (color) snackbar.color = color
      if (timeout) snackbar.timeout = (timeout === 0 ? -1 : timeout)
      if (text) snackbar.text = text
      if (actionText) snackbar.actionText = actionText
      if (action) snackbar.action = action
      // This should equate to a true or false exactly; using !! will turn something that evaluates to true/false but is not actually true/false into a true/false
      snackbar.active = (active == null ? true : !!active)
      context.commit('setSnackbar', snackbar)
    },
    async loadChecklist (context) {
      // Load the tracker for the given pidm, if one exists
      let trackerData = await context.dispatch('loadTracker')
      if (trackerData === false) {
        return false
      }
      // Build an object of the section statuses based on the section code
      let sectionStatus = {}
      if ('sections' in trackerData && Array.isArray(trackerData.sections) && trackerData.sections.length > 0) {
        for (const { section, status } of trackerData.sections) {
          sectionStatus[section] = status
        }
      }
      // Load the term
      let term = context.state.term
      // Prepare a hash object; the dependentSections are stored based on the ids but we want to use them as codes within the system
      // This hash will store the ID as the key and the code as the value; it will be populated as we go through the sections in order
      // All sections __should__ have their dependentSections be above them, so we should only need to go through the list once and all dependent sections should be in the hash when we need them
      let hash = {}
      // Load the active sections for the given term, ordered by the specified order number
      return this.$feathers.service('orientation/section').find({ query: { term, active: true, $limit: 50, $sort: { order: 1 } } }).then(({ data }) => {
        // Make a blank checklist array to populate as we go through the list
        let checklist = []
        // Extract the needed fields from each item in the retrieved data
        for (const { _id, code, label, dependentSections } of data) {
          // Add this section to the hash
          hash[_id] = code
          // Build the base object
          let item = { _id: _id, code, label, status: code in sectionStatus ? sectionStatus[code] : 'Locked', dependentSections: [] }
          // If we have a status from the tracker, use that
          if (dependentSections.length > 0) {
            // add each dependent section to the item using its code instead of id
            for (const id of dependentSections) {
              if (id in hash) {
                item.dependentSections.push(hash[id])
              }
            }
          }
          checklist.push(item)
        }
        // Set this default checklist
        context.commit('setChecklist', checklist)
        // Run the update to see if any sections need their status updated
        context.dispatch('updateSectionStatuses')
      })
    },
    getTracker (context) {
      let { trackerId } = context.state
      if (trackerId !== '') {
        return this.$feathers.service('orientation/tracker').get(trackerId)
      } else {
        return context.dispatch('loadTracker')
      }
    },
    async loadTracker (context) {
      const { data } = await this.$feathers.service('orientation/tracker').find({ query: { pidm: context.state.user.pidm } })
      if (data.length > 0) {
        const { _id, term, sections, blocks, actions, academics } = data[0]
        context.commit('setTrackerId', _id)
        context.commit('setTerm', term)
        context.commit('setAcademics', academics)
        if (sections) context.commit('setTrackerSections', sections)
        if (blocks) context.commit('setTrackerBlocks', blocks)
        if (actions && Array.isArray(actions)) {
          // Combine the actions in the to-do list with the ones in the database so we have the current details for all of them
          const aggregate = [
            { $match: { term } },
            { $unwind: '$actions' },
            { $match: { 'actions.title': { $in: actions.map(({ title }) => title) } } },
            { $project: {
              title: '$actions.title',
              link: '$actions.link',
              isExternalLink: '$actions.isExternalLink',
              isLanguageTest: '$actions.isLanguageTest',
              dialogText: '$actions.dialogText',
              linkText: '$actions.linkText',
              dismissText: '$actions.dismissText',
              doneText: '$actions.doneText',
              popupText: '$actions.popupText',
              openDate: '$actions.openDate'
            } }
          ]
          const { data: actionData } = await this.$feathers.service('orientation/section').find({ query: { aggregate } })
          const arr = []
          for (const action of actions) {
            const rec = actionData.find(({ title }) => title === action.title)
            arr.push({ ...action, ...rec })
          }
          context.commit('setActions', arr)
        }
        return data[0]
      } else {
        return false
      }
    },
    slideTracker (context, { section, slideNum, duration, sectionCompleted }) {
      // This function is called when a slide is loaded to save what the last slide was that the student viewed
      // It also stores a duration of how long they were looking at the previous slide (if there was one)
      // We are also updating if the section is completed (which means we do not store the slide num since it is beyond the length of the slide)
      const trackServ = this.$feathers.service('orientation/tracker')
      // Load the tracker data from the store
      const { id, sections } = context.state.tracker
      // Find the section that we need to update
      for (let i = 0; i < sections.length; i++) {
        if (sections[i].section === section) {
          let block = JSON.parse(JSON.stringify(sections[i]))
          if (sectionCompleted) {
            block.status = 'Complete'
            // Check to see if there are any actions that should be unlocked for the student
            for (let i = 0; i < context.state.checklist.length; i++) {
              if (context.state.checklist[i].code === section) {
                this.$feathers.service('orientation/section').get(context.state.checklist[i]._id).then(({ actions }) => {
                  if (actions != null && Array.isArray(actions) && actions.length > 0) {
                    let addAction = []
                    for (const row of actions) {
                      const { link, openDate, condition } = row
                      if (openDate) {
                        let dt = new Date(openDate)
                        let now = new Date()
                        if (dt > now) continue
                      }
                      if (condition != null && typeof (condition) === 'object' && 'id' in condition && 'itemIndex' in condition) {
                        let include = false
                        for (const { block, items } of context.state.tracker.blocks) {
                          if (JSON.stringify(block) === JSON.stringify(condition.id)) {
                            if (items[condition.itemIndex].status === 'Y') include = true
                          }
                        }
                        if (!include) continue
                      }
                      let included = false
                      for (let i = 0; i < context.state.actions.length; i++) {
                        if (context.state.actions[i].link === link) included = true
                      }
                      if (!included) {
                        addAction.push({ ...row, added: new Date() })
                      }
                    }
                    if (addAction.length > 0) {
                      trackServ.patch(id, { $push: { actions: addAction } }).then(({ actions }) => {
                        context.commit('setActions', actions)
                      })
                    }
                  }
                })
                break
              }
            }
          } else {
            if (block.status === 'Ready') {
              block.status = 'In-Progress'
            }
            block.lastSlide = slideNum
          }
          // Only update highSlide if the sction is not completed, if it does not exist or is not set in the tracker block, or if the new slide is higher than the previous high slide
          if (!sectionCompleted && (!('highSlide' in block) || !block.highSlide || slideNum > block.highSlide)) {
            block.highSlide = slideNum
          }
          if (duration != null && slideNum > 1) {
            // The duration is for the previous slide
            let durationSlide = slideNum - 1
            // If there is a value for the time spent on the slide already, then add the new time do what is there (it is a running total of the time spent on the slide)
            if (durationSlide in block.slideTime) block.slideTime[durationSlide] += duration
            // If the slide num is not in the slideTime array, then add it
            else block.slideTime[durationSlide] = duration
          }
          context.dispatch('updateProgressSection', block)
          trackServ.patch(id, { ['sections.' + i]: block }).then(({ sections }) => {
            context.commit('setTrackerSections', sections)
            context.dispatch('updateSectionStatuses')
          })
        }
      }
    },
    actionsViewed (context) {
      let patchObj = {}
      let needsPatch = false
      for (let i = 0; i < context.state.actions.length; i++) {
        if (!('viewed' in context.state.actions[i])) {
          patchObj['actions.' + i + '.viewed'] = new Date()
          needsPatch = true
        }
      }
      if (needsPatch) {
        const service = this.$feathers.service('orientation/tracker')
        // Load the tracker data from the store
        const id = context.state.tracker.id
        service.patch(id, patchObj).then(({ actions }) => {
          context.commit('setActions', actions)
        })
      }
    },
    updateSectionStatuses (context) {
      const trackServ = this.$feathers.service('orientation/tracker')
      // Load the tracker data from the store
      const { checklist } = context.state
      const { id, sections } = context.state.tracker
      let sectionStatus = {}
      for (const { section, status } of sections) {
        sectionStatus[section] = status
      }
      let updates = []
      for (let index = 0; index < checklist.length; index++) {
        const { code, status, dependentSections } = checklist[index]
        // We only need to update the ones that are locked
        if (status === 'Locked') {
          let isReady = true
          for (const depCode of dependentSections) {
            if (!(depCode in sectionStatus) || sectionStatus[depCode] !== 'Complete') {
              isReady = false
              break
            }
          }
          if (isReady) {
            context.commit('updateChecklistItem', { index, status: 'Ready' })
            updates.push(code)
          }
        }
      }
      if (updates.length > 0) {
        let updateObj = {}
        for (const code of updates) {
          let exists = false
          for (let i = 0; i < sections.length; i++) {
            if (sections[i].section === code) {
              updateObj['sections.' + i + '.status'] = 'Ready'
              exists = true
              break
            }
          }
          if (!exists) {
            if (!('$push' in updateObj)) updateObj.$push = { sections: [] }
            updateObj.$push.sections.push({ section: code, status: 'Ready' })
          }
          context.dispatch('updateProgressSection', { section: code, status: 'Ready' })
        }
        trackServ.patch(id, updateObj).then(({ sections }) => {
          context.commit('setTrackerSections', sections)
        })
      }
    },
    updateProgressSection (context, obj) {
      const progServ = this.$feathers.service('orientation/progress/section')
      progServ.find({ query: { pidm: context.state.user.pidm, section: obj.section } }).then(({ data }) => {
        if (data.length === 0) {
          progServ.create({ pidm: context.state.user.pidm, ...obj })
        } else {
          progServ.patch(data[0]._id, obj)
        }
      })
    }
    // trackProgress (context, { section, slideNum, duration }) {
    //   let { trackerId } = context.state
    //   const service = this.$feathers.service('orientation/tracker')
    //   service.get(trackerId).then(({ checklist }) => {
    //     let exists = false
    //     for (let i = 0; i < checklist.length; i++) {
    //       if (checklist[i].section === section) {
    //         let obj = { ['checklist.' + i + '.lastSlide']: slideNum }
    //         if (!('highSlide' in checklist[i]) || !checklist[i].highSlide || slideNum > checklist[i].highSlide) {
    //           obj['checklist.' + i + '.highSlide'] = slideNum
    //         }
    //         // Add the amount of time that the student was on the slide (if it was passed)
    //         if (duration) obj['checklist.' + i + '.slideTime.' + (slideNum - 1)] = duration
    //         // Only change the status if it is currently in the "Ready" state; other possible states include In-Progess and Completed (Only other status is Locked and they can't access so we shouldn't need to change that one)
    //         if (checklist[i].status === 'Ready') obj['checklist.' + i + '.status'] = 'In-Progress'
    //         service.patch(trackerId, obj)
    //         exists = true
    //       }
    //     }
    //     // If we get here then that means the section has not been added to the tracker
    //     if (!exists) service.patch(trackerId, { $push: { checklist: { section, status: 'In-Progress', lastSlide: slideNum } } })
    //   })
    //   for (let i = 0; i < context.state.checklist.length; i++) {
    //     if (context.state.checklist[i].code === section) {
    //       if (context.state.checklist[i].status === 'Ready') {
    //         context.commit('updateChecklistItem', { index: i, status: 'In-Progress', lastSlide: slideNum })
    //         break
    //       }
    //     }
    //   }
    // },
  //   completeSection (context, { section }) {
  //     let { trackerId } = context.state
  //     const service = this.$feathers.service('orientation/tracker')
  //     context.commit('updateChecklistItem', { code: section, status: 'Complete', lastSlide: 1 })
  //     service.get(trackerId).then(({ checklist }) => {
  //       let completed = {}
  //       let hash = {}
  //       for (let i = 0; i < checklist.length; i++) {
  //         if (checklist[i].section === section) {
  //           service.patch(trackerId, { ['checklist.' + i + '.status']: 'Complete', ['checklist.' + i + '.lastSlide']: 1 })
  //           completed[checklist[i].section] = true
  //         }
  //         if (checklist[i].status === 'Complete') {
  //           completed[checklist[i].section] = true
  //         } else if (checklist[i].status === 'Locked') {
  //           hash[checklist[i].section] = i
  //         }
  //       }
  //       let patch = {}
  //       let needsPatch = false
  //       for (let i = 0; i < context.state.checklist.length; i++) {
  //         if (context.state.checklist[i].status === 'Locked') {
  //           let ready = true
  //           for (let j = 0; j < context.state.checklist[i].dependentSections.length; j++) {
  //             if (!(context.state.checklist[i].dependentSections[j] in completed)) ready = false
  //           }
  //           if (ready) {
  //             context.commit('updateChecklistItem', { index: i, status: 'Ready' })
  //             let index = hash[context.state.checklist[i].code]
  //             if (index != null) {
  //               patch['checklist.' + index + '.status'] = 'Ready'
  //               needsPatch = true
  //             } else {
  //               if (!('$push' in patch)) patch.$push = { 'checklist': [] }
  //               patch.$push.checklist.push({ section: context.state.checklist[i].code, status: 'Ready' })
  //               needsPatch = true
  //             }
  //           }
  //         }
  //       }
  //       if (needsPatch) service.patch(trackerId, patch)
  //     })
  //   }
  }
})

export default store
